// Building blocks of the Dhātu lab and the person × number grid, after the reference design: large rounded
// panels, numbered step cards, pill groups with a navy selected state, and the page introduction.

import type { FC, KeyboardEvent, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Bi, Eyebrow } from '@/prakrit/components/common/bi'
import { hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'

export interface LangText {
    hi: string
    en: string
}

/** A large rounded panel on the off-white card colour. */
export const PANEL = 'rounded-3xl border border-border bg-card shadow-card'

/** The page width of the introduction and of the narrower labs. */
export const NARROW = 'mx-auto w-full max-w-6xl px-4 sm:px-6'

interface PageIntroProps {
    eyebrow: string
    title: LangText
    intro: LangText
    /** Shorter, simpler introduction for Kids mode (shown with the kids-only class, so nothing jumps). */
    kidsIntro?: LangText
    className?: string
}

export const PageIntro: FC<PageIntroProps> = ({ eyebrow, title, intro, kidsIntro, className }): ReactNode => (
    <header className={cn(NARROW, 'flex flex-col gap-2 pb-8 pt-6 sm:pb-10 sm:pt-10', className)}>
        <nav aria-label='Breadcrumb' className='-ml-1'>
            <Link
                href='/grammar'
                className='group inline-flex min-h-tap items-center gap-1.5 rounded-full px-1 text-sm text-muted-foreground transition-colors hover:text-saffron-strong'
            >
                <ArrowLeft className='size-4 transition-transform group-hover:-translate-x-0.5' aria-hidden='true' />
                <span lang='hi' className='hindi'>व्याकरण प्रयोगशाला</span>
                <span aria-hidden='true'>·</span>
                <span lang='en'>Grammar Lab</span>
            </Link>
        </nav>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className='flex flex-col gap-1'>
            <span lang='en' className='text-[2.125rem] leading-[1.15] text-foreground sm:text-[2.75rem]'>{title.en}</span>
            <span lang='hi' className='hindi text-lg font-medium leading-snug text-muted-foreground sm:text-xl'>{title.hi}</span>
        </h1>
        <Bi
            hi={intro.hi}
            en={intro.en}
            className={cn('mt-2 max-w-3xl gap-1 text-base leading-relaxed text-muted-foreground sm:text-lg', kidsIntro && 'adults-only')}
        />
        {kidsIntro && (
            <Bi
                hi={kidsIntro.hi}
                en={kidsIntro.en}
                className='kids-only mt-2 max-w-3xl gap-1 text-base leading-relaxed text-muted-foreground sm:text-lg'
            />
        )}
    </header>
)

interface StepCardProps {
    id: string
    step: number
    hi: string
    en: string
    children: ReactNode
    className?: string
}

/** "१ · धातु": one numbered step of the lab's left column. */
export const StepCard: FC<StepCardProps> = ({ id, step, hi, en, children, className }): ReactNode => (
    <section aria-labelledby={id} className={cn(PANEL, 'flex flex-col gap-3 p-4 sm:p-5', className)}>
        <h2 id={id} className='flex flex-wrap items-baseline gap-x-1.5 font-sans text-sm'>
            <span aria-hidden='true' className='hindi font-semibold text-muted-foreground'>{hiDigits(step)} ·</span>
            <span lang='hi' className='hindi font-semibold text-foreground'>{hi}</span>
            <span lang='en' className='text-xs text-muted-foreground'>{en}</span>
        </h2>
        {children}
    </section>
)

/** A small caption inside a step card ("Beginner", "More roots"). */
export const StepLabel: FC<{ hi: string, en: string, id?: string, className?: string }> = ({ hi, en, id, className }): ReactNode => (
    <p id={id} className={cn('flex flex-wrap items-baseline gap-x-1.5 text-xs text-muted-foreground', className)}>
        <span lang='hi' className='hindi font-medium text-foreground/80'>{hi}</span>
        <span aria-hidden='true'>·</span>
        <span lang='en'>{en}</span>
    </p>
)

/**
 * Arrow keys inside a row of pills (Left / Right, Home / End): focus moves and the pill is chosen, like a radio group.
 * Pills carry data-pill; disabled pills are skipped.
 */
export const pillKeyNav = (event: KeyboardEvent<HTMLElement>): void => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return

    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-pill]:not(:disabled)'))
    const index = items.indexOf(document.activeElement as HTMLButtonElement)

    if (items.length === 0 || index === -1) return

    event.preventDefault()

    let next = index

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = Math.min(items.length - 1, index + 1)
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = Math.max(0, index - 1)
    else if (event.key === 'Home') next = 0
    else next = items.length - 1

    items[next]?.focus()
    items[next]?.click()
}

export interface PillOption<T extends string> {
    value: T
    label: ReactNode
    /** Accessible name when the label alone is not enough (e.g. Hindi only). */
    title?: string
}

interface PillGroupProps<T extends string> {
    label: string
    value: T
    options: PillOption<T>[]
    onChange: (value: T) => void
    tone?: 'navy' | 'saffron'
    className?: string
}

/** Mutually exclusive pills in one rounded track (उत्तम · मध्यम · अन्य). */
export function PillGroup<T extends string>({ label, value, options, onChange, tone = 'navy', className }: PillGroupProps<T>): ReactNode {
    return (
        <div
            role='group'
            aria-label={label}
            onKeyDown={pillKeyNav}
            className={cn('inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-full border border-border bg-card p-1', className)}
        >
            {options.map(o => {
                const active = o.value === value

                return (
                    <button
                        key={o.value}
                        type='button'
                        data-pill=''
                        aria-pressed={active}
                        aria-label={o.title}
                        title={o.title}
                        tabIndex={active ? 0 : -1}
                        onClick={() => onChange(o.value)}
                        className={cn(
                            'inline-flex min-h-tap items-center justify-center gap-1.5 rounded-full px-3.5 text-sm font-medium transition-colors',
                            active
                                ? tone === 'navy' ? 'bg-foreground text-background shadow-sm' : 'bg-saffron text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        )}
                    >
                        {o.label}
                    </button>
                )
            })}
        </div>
    )
}

interface ChipProps {
    pressed: boolean
    onClick: () => void
    children: ReactNode
    title?: string
    className?: string
}

/** A single toggle chip (a tense); the selected one is filled saffron. */
export const ChipButton: FC<ChipProps> = ({ pressed, onClick, children, title, className }): ReactNode => (
    <button
        type='button'
        data-pill=''
        aria-pressed={pressed}
        title={title}
        onClick={onClick}
        className={cn(
            'inline-flex min-h-tap items-center gap-1.5 rounded-full border px-3.5 text-sm transition-colors',
            pressed
                ? 'border-saffron bg-saffron font-semibold text-primary-foreground shadow-sm'
                : 'border-border bg-card text-foreground hover:border-saffron/60 hover:bg-saffron/8',
            className
        )}
    >
        {children}
    </button>
)
